
import { toState } from '../redux';

import MeetHourJS from './_';

const MHConferenceErrors = MeetHourJS.errors.conference;
const MHConnectionErrors = MeetHourJS.errors.connection;

/**
 * Creates a {@link MHLocalTrack} model from the given device id.
 *
 * @param {string} type - The media type of track being created. Expected values
 * are "video" or "audio".
 * @param {string} deviceId - The id of the target media source.
 * @param {number} [timeout] - A timeout for the MeetHourJS.createLocalTracks function call.
 * @returns {Promise<MHLocalTrack>}
 */
export function createLocalTrack(type: string, deviceId: string, timeout: ?number) {
    return (
        MeetHourJS.createLocalTracks({
            cameraDeviceId: deviceId,
            devices: [ type ],


            firefox_fake_device:
                window.config && window.config.firefox_fake_device,
            micDeviceId: deviceId,
            timeout
        })
            .then(([ mHLocalTrack ]) => mHLocalTrack));
}

/**
 * Determines whether analytics is enabled in a specific redux {@code store}.
 *
 * @param {Function|Object} stateful - The redux store, state, or
 * {@code getState} function.
 * @returns {boolean} If analytics is enabled, {@code true}; {@code false},
 * otherwise.
 */
export function isAnalyticsEnabled(stateful: Function | Object) {
    const { disableThirdPartyRequests, analytics = {} } = toState(stateful)['features/base/config'];

    return !(disableThirdPartyRequests || analytics.disabled);
}

/**
 * Determines whether a specific {@link MHConferenceErrors} instance
 * indicates a fatal {@link MHConference} error.
 *
 * FIXME Figure out the category of errors defined by the function and describe
 * that category. I've currently named the category fatal because it appears to
 * be used in the cases of unrecoverable errors that necessitate a reload.
 *
 * @param {Object|string} error - The {@code MHConferenceErrors} instance to
 * categorize/classify or an {@link Error}-like object.
 * @returns {boolean} If the specified {@code MHConferenceErrors} instance
 * indicates a fatal {@code MHConference} error, {@code true}; otherwise,
 * {@code false}.
 */
export function isFatalMHConferenceError(error: Object | string) {
    const errorName = typeof error === 'string' ? error : error.name;

    return (
        errorName === MHConferenceErrors.FOCUS_DISCONNECTED
            || errorName === MHConferenceErrors.FOCUS_LEFT
            || errorName === MHConferenceErrors.ICE_FAILED
            || errorName === MHConferenceErrors.OFFER_ANSWER_FAILED
            || errorName === MHConferenceErrors.VIDEOBRIDGE_NOT_AVAILABLE);
}

/**
 * Determines whether a specific {@link MHConnectionErrors} instance
 * indicates a fatal {@link MHConnection} error.
 *
 * FIXME Figure out the category of errors defined by the function and describe
 * that category. I've currently named the category fatal because it appears to
 * be used in the cases of unrecoverable errors that necessitate a reload.
 *
 * @param {Object|string} error - The {@code MHConnectionErrors} instance to
 * categorize/classify or an {@link Error}-like object.
 * @returns {boolean} If the specified {@code MHConnectionErrors} instance
 * indicates a fatal {@code MHConnection} error, {@code true}; otherwise,
 * {@code false}.
 */
export function isFatalMHConnectionError(error: Object | string) {
    const errorName = typeof error === 'string' ? error : error.name;

    return (
        errorName === MHConnectionErrors.CONNECTION_DROPPED_ERROR
            || errorName === MHConnectionErrors.OTHER_ERROR
            || errorName === MHConnectionErrors.SERVER_ERROR);
}
